import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Video, Youtube, Users } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Sistema de',
    titleHighlight: 'Bonus',
    rewards: [{
      title: 'Streamers Oficiales',
      desc: 'Únete a nuestro programa de creadores. Transmite tus aventuras en MU Vanguard y obtén recompensas exclusivas, soporte directo y beneficios premium para tu audiencia.'
    }, {
      title: 'Creación de Contenido',
      desc: '¿Creas guías, videos o contenido viral? Recompensamos el talento. Los creadores con alto impacto y visualizaciones recibirán bonificaciones especiales mensuales.'
    }, {
      title: 'Programa de Referidos',
      desc: 'Líderes de gremio y jugadores influyentes: traigan a sus guerreros y amigos a la batalla. Recibirán recompensas escalables por cada jugador activo que inviten.'
    }]
  },
  en: {
    title: 'Bonus',
    titleHighlight: 'System',
    rewards: [{
      title: 'Official Streamers',
      desc: 'Join our creator program. Stream your adventures in MU Vanguard and get exclusive rewards, direct support, and premium benefits for your audience.'
    }, {
      title: 'Content Creation',
      desc: 'Do you create guides, videos, or viral content? We reward talent. Creators with high impact and views will receive special monthly bonuses.'
    }, {
      title: 'Referral Program',
      desc: 'Guild leaders and influential players: bring your warriors and friends to battle. You will receive scalable rewards for every active player you invite.'
    }]
  },
  pt: {
    title: 'Sistema de',
    titleHighlight: 'Bônus',
    rewards: [{
      title: 'Streamers Oficiais',
      desc: 'Junte-se ao nosso programa de criadores. Transmita suas aventuras no MU Vanguard e obtenha recompensas exclusivas, suporte direto e benefícios premium para seu público.'
    }, {
      title: 'Criação de Conteúdo',
      desc: 'Você cria guias, vídeos ou conteúdo viral? Recompensamos o talento. Criadores com alto impacto e visualizações receberão bônus mensais especiais.'
    }, {
      title: 'Programa de Indicação',
      desc: 'Líderes de guilda e jogadores influentes: tragam seus guerreiros e amigos para a batalha. Vocês receberão recompensas escaláveis por cada jogador ativo que convidarem.'
    }]
  }
};
const rewardIcons = [Video, Youtube, Users];
const rewardColors = ['from-blue-500/20 to-glowBlue/20', 'from-gold/20 to-yellow-600/20', 'from-purple-500/20 to-pink-500/20'];
const rewardBorders = ['group-hover:border-glowBlue/50', 'group-hover:border-gold/50', 'group-hover:border-purple-500/50'];
export function Rewards() {
  const [language, setLanguage] = useState<Language>('es');
  const t = translations[language];
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section className="py-16 md:py-24 relative z-10 bg-darkerBlue overflow-hidden" data-id="element-362">
      <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
      backgroundImage: 'linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)',
      backgroundSize: '100px 100px'
    }} data-id="element-363"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-364">
        <div className="text-center mb-16" data-id="element-365">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-id="element-366">
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-367">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-368" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-369">
          {t.rewards.map((reward, index) => {
          const Icon = rewardIcons[index];
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className={`glass-panel p-8 rounded-2xl text-center group border border-white/5 transition-all duration-500 hover:-translate-y-2 ${rewardBorders[index]}`} data-id="element-370">
                <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${rewardColors[index]} flex items-center justify-center mb-6 border border-white/10 group-hover:scale-110 transition-transform duration-500`} data-id="element-371">
                  <Icon className="w-10 h-10 text-white" data-id="element-372" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4 font-cinzel" data-id="element-373">
                  {reward.title}
                </h3>
                <p className="text-gray-400 leading-relaxed" data-id="element-374">{reward.desc}</p>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}